"use client";

import posthog from "posthog-js";
import { useEffect, useRef } from "react";
import type { ReaderReportType } from "@/lib/reports/report-type";

const THRESHOLDS = [25, 50, 75, 100] as const;

type Threshold = (typeof THRESHOLDS)[number];

interface StoryReadProgressTrackerProps {
  edition: ReaderReportType;
  storySlug: string;
  storyCategory?: string | null;
  storyMaterialType?: string | null;
  storyReportDate?: string | null;
}

/**
 * Fires a `story_read_progress` event once per 25/50/75/100% milestone reached
 * on a story detail page. Measures by `documentElement.scrollTop` vs
 * `scrollHeight - innerHeight`, so it reflects actual scroll depth regardless
 * of viewport size.
 *
 * A milestone is considered "reached" when the bottom of the viewport has
 * scrolled past that percentage of the scrollable article area. Ceiling case:
 * when content fits entirely in the viewport (no scroll possible), we fire
 * 100% immediately so short stories still register as read.
 */
export function StoryReadProgressTracker({
  edition,
  storySlug,
  storyCategory = null,
  storyMaterialType = null,
  storyReportDate = null,
}: StoryReadProgressTrackerProps) {
  const startedAtRef = useRef(0);
  const firedRef = useRef<Set<Threshold>>(new Set());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    startedAtRef.current = performance.now();
    firedRef.current = new Set();

    const baseProps = {
      edition,
      story_slug: storySlug,
      story_category: storyCategory,
      story_material_type: storyMaterialType,
      story_report_date: storyReportDate,
      content_type: "story_detail",
    };

    const fire = (milestone: Threshold) => {
      if (firedRef.current.has(milestone)) {
        return;
      }
      firedRef.current.add(milestone);
      posthog.capture("story_read_progress", {
        ...baseProps,
        progress_percent: milestone,
        time_to_progress_ms: Math.round(
          performance.now() - startedAtRef.current
        ),
        viewport_height: window.innerHeight,
        scroll_height: document.documentElement.scrollHeight,
      });
    };

    const evaluate = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrollableDistance = Math.max(scrollHeight - viewportHeight, 0);

      if (scrollableDistance === 0) {
        // Story fits entirely in viewport — count as fully read
        for (const threshold of THRESHOLDS) {
          fire(threshold);
        }
        return;
      }

      const scrolled = Math.min(window.scrollY + viewportHeight, scrollHeight);
      const percent = (scrolled / scrollHeight) * 100;

      for (const threshold of THRESHOLDS) {
        if (percent >= threshold) {
          fire(threshold);
        }
      }
    };

    evaluate();
    const handleScroll = () => {
      window.requestAnimationFrame(evaluate);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", evaluate);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", evaluate);
    };
  }, [edition, storySlug, storyCategory, storyMaterialType, storyReportDate]);

  return null;
}
